import { Fragment } from "react"
import { Link } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type {
  BookingDetails,
  BookingFormData,
  BookingStatusChange,
  BookingStatusView,
} from "../bookingCalendarApi"
import type { MetadataEntry } from "../lib/bookingMetadata"
import { describeFormData, describeMetadata, describeScreening, matchedPersonHref } from "../lib/bookingMetadata"
import {
  BookingRequestStatus,
  actorLabel,
  statusBadgeClass,
  statusTooltip,
  statusLabel,
} from "../lib/bookingStatus"
import { describeDailyLimitOverride } from "../lib/dailyLimit"
import { describeDoubleBookingOverride } from "../lib/doubleBooking"
import { BookingDocumentsSection } from "./BookingDocumentsSection"
import { BookingNotesSection } from "./BookingNotesSection"

/**
 * Everything the appointment dialog says about one booking: its status and why, the host and
 * contact, what the applicant submitted and Cal's booking details. Rendered as siblings inside the
 * dialog's content, so it adds no wrapper of its own. The status history lives in the dialog's footer;
 * see `BookingStatusHistory`.
 */
export function BookingDetailsPanel({
  booking,
  staffName,
  formData,
  statusView,
  attendeeEmail,
  emailIsPlaceholder,
  onFollow,
  canViewPeople = false,
}: {
  booking: BookingDetails
  /** The team member hosting it, as the grid labels them. */
  staffName: string | undefined
  formData: BookingFormData | undefined
  statusView: BookingStatusView | undefined
  attendeeEmail: string | undefined
  emailIsPlaceholder: boolean
  onFollow: (bookingUid: string) => void
  /** Holds `people:view`, so the matched CMS person can be linked to. */
  canViewPeople?: boolean
}) {
  const attendee = booking.attendees[0]

  // The two halves of "what this booking was made with", from the two systems that hold them: the
  // form the applicant filled in, which is CMS's, and Cal's own booking metadata, which is now the
  // confirmation code and little else. Staff on the phone are asked for exactly these.
  const submitted = describeFormData(formData)
  const metadata = describeMetadata(booking.metadata)
  const screening = describeScreening(formData)
  const personHref = matchedPersonHref(formData, canViewPeople)

  return (
    <>
        {/* Every row is drawn, empty or not: somebody reading an appointment out over the phone has
            to be able to tell a number this booking does not have from one the dialog left out. */}
        <dl className="grid grid-cols-[10rem_minmax(0,1fr)] gap-x-3 gap-y-2 break-words text-sm">
          <dt className="text-muted-foreground">Status</dt>
          <dd className="flex flex-wrap items-center gap-1.5">
            {statusView && (
              <Badge
                variant="outline"
                className={`rounded text-[0.7rem] ${statusBadgeClass(statusView.status)}`}
                title={statusTooltip(statusView.status, statusView.statusReason)}
              >
                {statusLabel(statusView.status)}
              </Badge>
            )}
            {statusView?.doubleBookingOverride && (
              <Badge
                variant="outline"
                className="rounded border-amber-400 bg-amber-50 text-[0.7rem] text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                title={describeDoubleBookingOverride(statusView)}
              >
                Double-booked (override)
              </Badge>
            )}
            {statusView?.dailyLimitOverride && (
              <Badge
                variant="outline"
                className="rounded border-amber-400 bg-amber-50 text-[0.7rem] text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                title={describeDailyLimitOverride(statusView)}
              >
                Past daily limit (override)
              </Badge>
            )}
          </dd>

          {statusView && <StatusDetails view={statusView} onFollow={onFollow} />}

          <dt className="text-muted-foreground">Host</dt>
          <dd>{staffName ?? statusView?.hostName ?? <NotProvided />}</dd>

          <dt className="text-muted-foreground">Phone</dt>
          <dd>{attendee?.phoneNumber || <NotProvided />}</dd>

          <dt className="text-muted-foreground">Email</dt>
          <dd className="break-all">
            {emailIsPlaceholder && attendeeEmail
              ? <PlaceholderEmail address={attendeeEmail} />
              : attendeeEmail || <NotProvided />}
          </dd>

          <dt className="text-muted-foreground">Location</dt>
          <dd>{booking.location || <NotProvided />}</dd>
        </dl>

        {/* What the applicant actually typed. Its own section above the Cal metadata, because it is
            the half staff are reading for and it comes from the system that is the record of it. */}
        {submitted.length > 0 && (
          <section className="border-t pt-3">
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Submitted information
            </h3>
            <DetailRows entries={submitted} />
          </section>
        )}

        {/* What the household screening found, and who it matched (AB#766). Only with a request
            behind the booking, like the section above. */}
        {screening.length > 0 && (
          <section className="border-t pt-3">
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Programs
            </h3>
            <DetailRows entries={screening} />
            {personHref && (
              <dl className="mt-2 grid grid-cols-[10rem_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
                <dt className="text-muted-foreground">CMS person</dt>
                <dd>
                  <Link to={personHref} className="text-primary underline-offset-4 hover:underline">
                    Open matched person
                  </Link>
                </dd>
              </dl>
            )}
          </section>
        )}

        {metadata.length > 0 && (
          <section className="border-t pt-3">
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Booking details
            </h3>
            <DetailRows entries={metadata} />
          </section>
        )}

        {booking.attendees.length > 1 && (
          <p className="text-xs text-muted-foreground">
            +{booking.attendees.length - 1} other attendee{booking.attendees.length > 2 ? "s" : ""}
          </p>
        )}

        <BookingNotesSection requestId={formData?.requestId} />
        <BookingDocumentsSection requestId={formData?.requestId} />
    </>
  )
}

/**
 * The booking's status changes, folded away behind a toggle. Drawn in the appointment dialog's
 * footer, on its own row under the buttons.
 *
 * Drawn for every booking CMS has a row for, history or not: rows are only written when the status
 * changes, so a booking still at Booked has none, and a toggle that comes and goes reads as missing.
 */
export function BookingStatusHistory({ history, isError, className }: {
  /** Undefined while loading. */
  history: BookingStatusChange[] | undefined
  isError: boolean
  className?: string
}) {
  return (
    <details className={cn("text-left text-sm", className)}>
      <summary className="cursor-pointer text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Status history
      </summary>
      {isError ? (
        <p role="alert" className="mt-2 text-xs text-red-700">The status history could not be loaded.</p>
      ) : !history ? (
        <p className="mt-2 text-xs text-muted-foreground">Loading…</p>
      ) : history.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">No status changes yet.</p>
      ) : (
        <ol className="mt-2 max-h-40 space-y-1.5 overflow-y-auto">
          {history.map((change, index) => (
            <li key={`${change.changedAt}-${index}`} className="text-xs">
              <span className="font-medium">{statusLabel(change.toStatus)}</span>
              <span className="text-muted-foreground">
                {" · "}{formatTimestamp(change.changedAt)}{" · "}{describeChangedBy(change.changedBy)}
              </span>
              {change.reason && <span className="block text-muted-foreground">{change.reason}</span>}
            </li>
          ))}
        </ol>
      )}
    </details>
  )
}

/** The facts `BookingSummary` shows. Every field may be missing; missing ones still draw a row. */
export interface BookingSummaryFields {
  /** The only id staff see. The CMS request id and Cal's booking uid are left out: they are for developers. */
  referenceCode: string
  customerName: string | null
  hostName: string | null
  /** Already a display label, e.g. "Intake". */
  eventType: string | null
  /** Already a display label for start–end, in the site's zone. */
  when: string | null
  siteName: string | null
  status: number | null
}

/**
 * A compact description of one booking for surfaces that have the CMS request but not necessarily
 * the Cal booking behind it — the Booking Review modal. Same row layout and status badge as the
 * appointment dialog's panel above, so a booking reads the same wherever it is opened.
 */
export function BookingSummary({ fields }: { fields: BookingSummaryFields }) {
  return (
    <dl className="grid grid-cols-[10rem_minmax(0,1fr)] gap-x-3 gap-y-2 break-words text-sm">
      <dt className="text-muted-foreground">Status</dt>
      <dd className="flex flex-wrap items-center gap-1.5">
        {fields.status === null
          ? <NotProvided />
          : (
            <Badge variant="outline" className={`rounded text-[0.7rem] ${statusBadgeClass(fields.status)}`}>
              {statusLabel(fields.status)}
            </Badge>
          )}
      </dd>

      <dt className="text-muted-foreground">Reference</dt>
      <dd className="font-mono">{fields.referenceCode}</dd>

      <dt className="text-muted-foreground">Customer</dt>
      <dd>{fields.customerName ?? <NotProvided />}</dd>

      <dt className="text-muted-foreground">Host</dt>
      <dd>{fields.hostName ?? <NotProvided />}</dd>

      <dt className="text-muted-foreground">Service</dt>
      <dd>{fields.eventType ?? <NotProvided />}</dd>

      <dt className="text-muted-foreground">When</dt>
      <dd>{fields.when ?? <NotProvided />}</dd>

      <dt className="text-muted-foreground">Site</dt>
      <dd>{fields.siteName ?? <NotProvided />}</dd>
    </dl>
  )
}

/**
 * The rows under Status that explain it: why, who cancelled, and where a reschedule went. Rendered
 * inside the dialog's main list, so each is a `dt`/`dd` pair.
 */
function StatusDetails({ view, onFollow }: { view: BookingStatusView; onFollow: (bookingUid: string) => void }) {
  const cancelledBy = actorLabel(view.cancelledBy)
  const reason = view.status === BookingRequestStatus.Cancelled ? view.cancellationReason : view.statusReason

  return (
    <>
      {cancelledBy && view.status === BookingRequestStatus.Cancelled && (
        <>
          <dt className="text-muted-foreground">Cancelled by</dt>
          <dd>{cancelledBy}</dd>
        </>
      )}

      {reason && (
        <>
          <dt className="text-muted-foreground">Reason</dt>
          <dd className="break-words">{reason}</dd>
        </>
      )}

      {/* The one exception to "one appointment per host at a time", so who made it and why are on the
          booking itself rather than only in its history. */}
      {view.doubleBookingOverride && (
        <>
          <dt className="text-muted-foreground">Double-booked</dt>
          <dd className="break-words">
            {view.doubleBookingOverrideReason || "No reason recorded"}
            {(view.doubleBookingOverrideBy || view.doubleBookingOverrideAt) && (
              <span className="text-muted-foreground">
                {" · "}
                {[
                  view.doubleBookingOverrideBy && describeChangedBy(view.doubleBookingOverrideBy),
                  view.doubleBookingOverrideAt && formatTimestamp(view.doubleBookingOverrideAt),
                ].filter(Boolean).join(", ")}
              </span>
            )}
          </dd>
        </>
      )}

      {/* A manager booked the host past their daily limit (AB#606): who and why, on the booking. */}
      {view.dailyLimitOverride && (
        <>
          <dt className="text-muted-foreground">Past daily limit</dt>
          <dd className="break-words">
            {view.dailyLimitOverrideReason || "No reason recorded"}
            {(view.dailyLimitOverrideBy || view.dailyLimitOverrideAt) && (
              <span className="text-muted-foreground">
                {" · "}
                {[
                  view.dailyLimitOverrideBy && describeChangedBy(view.dailyLimitOverrideBy),
                  view.dailyLimitOverrideAt && formatTimestamp(view.dailyLimitOverrideAt),
                ].filter(Boolean).join(", ")}
              </span>
            )}
          </dd>
        </>
      )}

      {view.statusChangedAt && (
        <>
          <dt className="text-muted-foreground">Status updated</dt>
          <dd>
            {formatTimestamp(view.statusChangedAt)}
            {view.statusChangedBy && <span className="text-muted-foreground"> · {describeChangedBy(view.statusChangedBy)}</span>}
          </dd>
        </>
      )}

      {view.rescheduledToCalBookingUid && (
        <>
          <dt className="text-muted-foreground">Rescheduled to</dt>
          <dd>
            <Button variant="link" className="h-auto p-0 text-sm" onClick={() => onFollow(view.rescheduledToCalBookingUid!)}>
              Open the new booking
            </Button>
          </dd>
        </>
      )}

      {view.rescheduledFromCalBookingUid && (
        <>
          <dt className="text-muted-foreground">Rescheduled from</dt>
          <dd>
            <Button variant="link" className="h-auto p-0 text-sm" onClick={() => onFollow(view.rescheduledFromCalBookingUid!)}>
              Open the earlier booking
            </Button>
          </dd>
        </>
      )}
    </>
  )
}

/** A stored UTC instant, in the reader's own locale and zone. */
function formatTimestamp(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
}

/** The history's actor, with CMS's and Cal's own entries named rather than shown as codes. */
function describeChangedBy(changedBy: string): string {
  if (changedBy === "system") return "automatically"
  if (changedBy === "cal") return "in the scheduling system"
  return changedBy
}

/**
 * A labelled list of rows, for either of the dialog's two detail sections.
 *
 * Not capped: the metadata key set is open, but the dialog scrolls as one and pins its footer, so
 * however many pairs a booking carries the buttons stay reachable without a scroller inside a scroller.
 */
function DetailRows({ entries }: { entries: readonly MetadataEntry[] }) {
  return (
    <dl className="grid grid-cols-[10rem_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
      {entries.map(entry => (
        <Fragment key={entry.key}>
          <dt className="text-muted-foreground">{entry.label}</dt>
          <dd className={entry.mono && entry.value ? "font-mono break-all" : "break-words"}>
            {entry.value ?? <NotProvided />}
          </dd>
        </Fragment>
      ))}
    </dl>
  )
}

/**
 * The right-hand side of a row this booking has nothing for.
 *
 * A dash rather than blank space, so the row reads as answered-with-nothing rather than as a
 * rendering that gave out halfway, and labelled for a screen reader, which has no dash to read.
 */
function NotProvided() {
  return (
    <span className="text-muted-foreground">
      <span aria-hidden="true">—</span>
      <span className="sr-only">Not provided</span>
    </span>
  )
}

/**
 * The Email row for a booking made without one.
 *
 * Cal will not take a booking with no attendee address, so CMS supplies a per-booking placeholder
 * that looks exactly like a real address — see `isPlaceholderEmail`. Printing it would invite staff
 * to read it out to the household or write to it, and nothing sent there arrives anywhere, so the
 * row says what the address means instead of what it is.
 *
 * Not folded into `NotProvided`: an appointment CMS has no email for at all is a different thing to
 * say over the phone than one the applicant was asked for an email and had none to give.
 *
 * The stored address is still on the element's title, because reconciling a booking against Cal by
 * hand is the one job that needs the literal value.
 */
function PlaceholderEmail({ address }: { address: string }) {
  return (
    <span className="text-muted-foreground" title={address}>
      No email given
      <span className="sr-only"> — the scheduling system holds a placeholder address</span>
    </span>
  )
}
